import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
  OAuthApi,
} from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';

export interface CodebaseScope {
  repos: Array<{ repo: string; branches: string[] }>;
}

export interface CodebaseAskRequest {
  question: string;
  repoHints?: string[];
  branchHints?: string[];
  priorContext?: string;
}

export interface CodebaseAnswer {
  answer: string;
  truncated: boolean;
  reposConsulted: string[];
  branchesConsulted: string[];
  filesConsulted: string[];
}

export interface CodebaseAskApi {
  /** The repos and branches the signed-in person can ask about; empty when none. */
  getScope(): Promise<CodebaseScope>;
  /** Asks one question; the signal only stops the browser waiting. */
  ask(
    request: CodebaseAskRequest,
    options?: { signal?: AbortSignal },
  ): Promise<CodebaseAnswer>;
}

export const codebaseAskApiRef = createApiRef<CodebaseAskApi>({
  id: 'app.codebase-ask',
});

/**
 * Ask the Codebase is not available to this session: no audience is
 * configured, or there is no Microsoft session to get a token from silently.
 */
export class CodebaseAskUnavailableError extends Error {
  name = 'CodebaseAskUnavailableError';
}

/**
 * Calls the mcp-codebase plugin's Hub routes. `fetchApi` sends the Backstage
 * token in Authorization; the person's Entra `code.ask` token, which the
 * backend gate checks group membership with, goes in X-Entra-Authorization.
 */
export class CodebaseAskClient implements CodebaseAskApi {
  constructor(
    private readonly deps: {
      discoveryApi: DiscoveryApi;
      fetchApi: FetchApi;
      microsoftAuthApi: OAuthApi;
      /** `app.askAi.codebaseAudience`, e.g. `api://<hub-app-id>`. */
      codebaseAudience?: string;
    },
  ) {}

  async getScope(): Promise<CodebaseScope> {
    const response = await this.request('scope', {});
    return response.json();
  }

  async ask(
    request: CodebaseAskRequest,
    options: { signal?: AbortSignal } = {},
  ): Promise<CodebaseAnswer> {
    const response = await this.request('ask', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
      signal: options.signal,
    });
    return response.json();
  }

  private async entraToken(): Promise<string> {
    const { codebaseAudience, microsoftAuthApi } = this.deps;
    if (!codebaseAudience) {
      throw new CodebaseAskUnavailableError(
        'Ask the Codebase is not configured',
      );
    }
    // optional: a silent refresh only. Never start a sign-in from the drawer.
    const token = await microsoftAuthApi.getAccessToken(
      `${codebaseAudience}/code.ask`,
      { optional: true },
    );
    if (!token) {
      throw new CodebaseAskUnavailableError('No Microsoft session for Ask AI');
    }
    return token;
  }

  private async request(
    path: string,
    init: { headers?: Record<string, string> } & Omit<RequestInit, 'headers'>,
  ): Promise<Response> {
    const token = await this.entraToken();
    const baseUrl = await this.deps.discoveryApi.getBaseUrl('mcp-codebase');
    const response = await this.deps.fetchApi.fetch(`${baseUrl}/${path}`, {
      ...init,
      headers: { ...init.headers, 'X-Entra-Authorization': `Bearer ${token}` },
    });
    if (!response.ok) throw await ResponseError.fromResponse(response);
    return response;
  }
}
